/**
 * Конструктор шаблонов карточек РД (templates.rd_card_*).
 * Черновик: секции, поля, привязки к req.request / req.request_bow; публикация.
 * Разметка списка и боковой панели — в одном стиле с CardTemplateManagement (ОИ).
 */

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/hooks/use-toast";
import { rdCardTemplatesApi, type RdCardTemplateUpdatePayload } from "@/services/rd-card-api.service";
import { useFieldWidgetTypesQuery, widgetTypesToOptions } from "@/hooks/useFieldWidgetTypes";
import type {
  RdCardFieldType,
  RdCardTemplateField,
  RdCardTemplateRow,
  RdCardTemplateSection,
  RdCardTemplateWithDetails,
} from "@/types/rd-card-template";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetFooter,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  CardTemplateTableListSkeleton,
  RdCardTemplateDetailBodySkeleton,
} from "@/components/card/shared";
import { OrganizationKlsCombobox } from "./OrganizationKlsCombobox";
import { RdRequestTypeKlsCombobox } from "./RdRequestTypeKlsCombobox";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  STATUS_LABELS,
  type CardTemplateRestoreRequest,
  type CardTemplateStatus,
} from "@/types/card-template";
import {
  Archive,
  CheckCircle2,
  Copy,
  HelpCircle,
  Plus,
  Pencil,
  RefreshCw,
  RotateCcw,
  Trash2,
  Upload,
} from "lucide-react";

const FIELD_TYPES = [
  "string",
  "text",
  "number",
  "date",
  "boolean",
  "json",
  "classifier",
  "classifier_tree",
  "file",
  "geometry_link",
] as const;

const STATUS_BADGE_VARIANT: Record<
  CardTemplateStatus,
  "default" | "secondary" | "destructive"
> = {
  draft: "secondary",
  published: "default",
  archived: "destructive",
};

const RD_CARD_TEMPLATE_SHEET_WIDTH_KEY = "rd-card-template-sheet-width-v1";
const SHEET_WIDTH_MIN = 360;
const SHEET_WIDTH_PRESETS = { narrow: 480, standard: 768, wide: 1024 } as const;

function maxSheetWidthPx(): number {
  if (typeof window === "undefined") return SHEET_WIDTH_PRESETS.wide * 2;
  return Math.max(SHEET_WIDTH_MIN, Math.floor(window.innerWidth * 0.96));
}

function clampSheetWidth(w: number): number {
  return Math.max(SHEET_WIDTH_MIN, Math.min(w, maxSheetWidthPx()));
}

function readStoredRdSheetWidth(): number {
  if (typeof window === "undefined") return SHEET_WIDTH_PRESETS.standard;
  try {
    const raw = localStorage.getItem(RD_CARD_TEMPLATE_SHEET_WIDTH_KEY);
    const n = raw ? parseInt(raw, 10) : NaN;
    if (!Number.isFinite(n)) return SHEET_WIDTH_PRESETS.standard;
    return clampSheetWidth(n);
  } catch {
    return SHEET_WIDTH_PRESETS.standard;
  }
}

function formatPublishedDate(d?: string | null): string {
  return d ? new Date(d).toLocaleDateString("ru-RU") : "—";
}

type RdRestoreMode = "simple" | "replace" | "non_default";

export default function RdCardTemplateManagement() {
  const { toast } = useToast();
  const qc = useQueryClient();
  const [statusFilter, setStatusFilter] = useState<string>("__all__");
  const [createOpen, setCreateOpen] = useState(false);
  const [newCode, setNewCode] = useState("");
  const [newName, setNewName] = useState("");
  const [newReqTypeKlsId, setNewReqTypeKlsId] = useState<number | "">("");
  const [newOrgKlsId, setNewOrgKlsId] = useState<number | "">("");

  const [detailOpen, setDetailOpen] = useState(false);
  const [selected, setSelected] = useState<RdCardTemplateRow | null>(null);

  const [sectionOpen, setSectionOpen] = useState(false);
  const [secCode, setSecCode] = useState("");
  const [secName, setSecName] = useState("");

  const [fieldOpen, setFieldOpen] = useState(false);
  const [fk, setFk] = useState("");
  const [flabel, setFlabel] = useState("");
  const [qualId, setQualId] = useState("1");
  const [fType, setFType] = useState<string>("string");

  const [bindOpen, setBindOpen] = useState(false);
  const [bindFieldId, setBindFieldId] = useState<number | null>(null);
  const [bindTable, setBindTable] = useState("request");
  const [bindCol, setBindCol] = useState("");

  const [editSection, setEditSection] = useState<RdCardTemplateSection | null>(null);
  const [editSecCode, setEditSecCode] = useState("");
  const [editSecName, setEditSecName] = useState("");
  const [deleteSection, setDeleteSection] = useState<RdCardTemplateSection | null>(null);

  const [editField, setEditField] = useState<RdCardTemplateField | null>(null);
  const [editFk, setEditFk] = useState("");
  const [editFlabel, setEditFlabel] = useState("");
  const [editQualId, setEditQualId] = useState("1");
  const [editFType, setEditFType] = useState<string>("string");
  const [editSectionId, setEditSectionId] = useState<string>("none");
  const [deleteField, setDeleteField] = useState<RdCardTemplateField | null>(null);

  const [cloneOpen, setCloneOpen] = useState(false);
  const [cloneSource, setCloneSource] = useState<RdCardTemplateRow | null>(null);
  const [cloneCode, setCloneCode] = useState("");

  /** Удаление черновика или архива — текст диалога зависит от fromArchive (как у ОИ). */
  const [deleteTarget, setDeleteTarget] = useState<{
    id: number;
    fromArchive: boolean;
  } | null>(null);

  const [restoreTarget, setRestoreTarget] = useState<RdCardTemplateRow | null>(null);
  const [restoreMode, setRestoreMode] = useState<RdRestoreMode>("simple");
  const [restoreReason, setRestoreReason] = useState("");
  const importInputRef = useRef<HTMLInputElement>(null);

  const [sheetWidthPx, setSheetWidthPx] = useState(readStoredRdSheetWidth);
  const [genName, setGenName] = useState("");
  const [genDesc, setGenDesc] = useState("");
  const [genReqTypeKlsId, setGenReqTypeKlsId] = useState<number | "">("");
  const [genOrgKlsId, setGenOrgKlsId] = useState<number | "">("");
  const [genDefault, setGenDefault] = useState(false);
  const [genActive, setGenActive] = useState(true);

  const listQ = useQuery({
    queryKey: ["rd-card-templates", statusFilter],
    queryFn: () =>
      rdCardTemplatesApi.list({
        limit: 100,
        status: statusFilter === "__all__" ? undefined : statusFilter,
      }),
  });

  const fieldWidgetTypesQ = useFieldWidgetTypesQuery({ staleTime: 60_000 });
  const widgetTypeOptions = useMemo(
    () => widgetTypesToOptions(fieldWidgetTypesQ.data, FIELD_TYPES),
    [fieldWidgetTypesQ.data],
  );

  const detailQ = useQuery({
    queryKey: ["rd-card-template", selected?.id],
    queryFn: () => rdCardTemplatesApi.get(selected!.id),
    enabled: detailOpen && !!selected,
  });

  const createM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.create({
        code: newCode.trim(),
        name: newName.trim(),
        request_type_kls_id: newReqTypeKlsId === "" ? null : newReqTypeKlsId,
        organization_kls_id: newOrgKlsId === "" ? null : newOrgKlsId,
      }),
    onSuccess: () => {
      toast({ title: "Черновик создан" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      setCreateOpen(false);
      setNewCode("");
      setNewName("");
      setNewReqTypeKlsId("");
      setNewOrgKlsId("");
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const publishM = useMutation({
    mutationFn: (id: number) => rdCardTemplatesApi.publish(id),
    onSuccess: () => {
      toast({ title: "Опубликовано" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      qc.invalidateQueries({ queryKey: ["rd-card-template"] });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const archiveM = useMutation({
    mutationFn: (id: number) => rdCardTemplatesApi.archive(id),
    onSuccess: () => {
      toast({ title: "Шаблон архивирован" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      qc.invalidateQueries({ queryKey: ["rd-card-template"] });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const restoreM = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: CardTemplateRestoreRequest }) =>
      rdCardTemplatesApi.restore(id, payload),
    onSuccess: () => {
      toast({ title: "Шаблон восстановлен из архива" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      qc.invalidateQueries({ queryKey: ["rd-card-template"] });
      setRestoreTarget(null);
      setRestoreReason("");
      setRestoreMode("simple");
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const importM = useMutation({
    mutationFn: ({ text, onConflict }: { text: string; onConflict: "error" | "skip" | "suffix" }) => {
      const bundle = JSON.parse(text) as Record<string, unknown>;
      return rdCardTemplatesApi.importTemplatesJson(bundle, onConflict);
    },
    onSuccess: (res) => {
      const created = res.results.filter((r) => r.outcome === "created").length;
      toast({
        title: "Импорт завершён",
        description: `Создано черновиков: ${created}, всего записей: ${res.results.length}`,
      });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
    },
    onError: (e: Error) =>
      toast({ title: "Импорт JSON", description: e.message, variant: "destructive" }),
  });

  const deleteM = useMutation({
    mutationFn: (id: number) => rdCardTemplatesApi.delete(id),
    onSuccess: (_, id) => {
      toast({ title: "Шаблон удалён" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      qc.invalidateQueries({ queryKey: ["rd-card-template", id] });
      setDeleteTarget(null);
      if (selected?.id === id) {
        setDetailOpen(false);
        setSelected(null);
      }
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const cloneM = useMutation({
    mutationFn: ({ id, code }: { id: number; code: string }) => rdCardTemplatesApi.clone(id, code),
    onSuccess: (data) => {
      toast({ title: "Создан черновик-копия", description: `Код: ${data.code}` });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      setCloneOpen(false);
      setCloneSource(null);
      setCloneCode("");
      setSelected(data);
      setDetailOpen(true);
      qc.invalidateQueries({ queryKey: ["rd-card-template", data.id] });
    },
    onError: (e: Error) => toast({ title: "Ошибка клонирования", description: e.message, variant: "destructive" }),
  });

  const beginClone = (row: RdCardTemplateRow) => {
    setCloneSource(row);
    setCloneCode(`${row.code}_copy`);
    setCloneOpen(true);
  };

  const updateTplM = useMutation({
    mutationFn: ({ id, body }: { id: number; body: RdCardTemplateUpdatePayload }) =>
      rdCardTemplatesApi.update(id, body),
    onSuccess: (_, vars) => {
      toast({ title: "Шаблон обновлён" });
      qc.invalidateQueries({ queryKey: ["rd-card-templates"] });
      qc.invalidateQueries({ queryKey: ["rd-card-template", vars.id] });
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  useEffect(() => {
    try {
      localStorage.setItem(RD_CARD_TEMPLATE_SHEET_WIDTH_KEY, String(sheetWidthPx));
    } catch {
      /* ignore */
    }
  }, [sheetWidthPx]);

  useEffect(() => {
    const onWin = () => setSheetWidthPx((w) => clampSheetWidth(w));
    window.addEventListener("resize", onWin);
    return () => window.removeEventListener("resize", onWin);
  }, []);

  const addSecM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.addSection(selected!.id, {
        code: secCode.trim(),
        name: secName.trim(),
      }),
    onSuccess: () => {
      toast({ title: "Секция добавлена" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setSectionOpen(false);
      setSecCode("");
      setSecName("");
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const addFieldM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.addField(selected!.id, {
        field_key: fk.trim(),
        label: flabel.trim(),
        field_type: fType as RdCardFieldType,
        qual_id: Number(qualId) || 1,
      }),
    onSuccess: () => {
      toast({ title: "Поле добавлено" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setFieldOpen(false);
      setFk("");
      setFlabel("");
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const bindM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.upsertBinding(selected!.id, bindFieldId!, {
        target_schema: "req",
        target_table: bindTable,
        target_column: bindCol.trim(),
      }),
    onSuccess: () => {
      toast({ title: "Привязка сохранена" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setBindOpen(false);
      setBindFieldId(null);
      setBindCol("");
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const updateSecM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.updateSection(selected!.id, editSection!.id, {
        code: editSecCode.trim(),
        name: editSecName.trim(),
      }),
    onSuccess: () => {
      toast({ title: "Секция обновлена" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setEditSection(null);
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const deleteSecM = useMutation({
    mutationFn: (sid: number) => rdCardTemplatesApi.deleteSection(selected!.id, sid),
    onSuccess: () => {
      toast({ title: "Секция удалена" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setDeleteSection(null);
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const updateFieldM = useMutation({
    mutationFn: () =>
      rdCardTemplatesApi.updateField(selected!.id, editField!.id, {
        field_key: editFk.trim(),
        label: editFlabel.trim(),
        qual_id: Number(editQualId) || 1,
        field_type: editFType as RdCardFieldType,
        section_id: editSectionId === "none" ? null : Number(editSectionId),
      }),
    onSuccess: () => {
      toast({ title: "Поле обновлено" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setEditField(null);
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const deleteFieldM = useMutation({
    mutationFn: (fid: number) => rdCardTemplatesApi.deleteField(selected!.id, fid),
    onSuccess: () => {
      toast({ title: "Поле удалено" });
      qc.invalidateQueries({ queryKey: ["rd-card-template", selected!.id] });
      setDeleteField(null);
    },
    onError: (e: Error) => toast({ title: "Ошибка", description: e.message, variant: "destructive" }),
  });

  const rows = listQ.data?.items ?? [];

  function openDetail(row: RdCardTemplateRow) {
    setSelected(row);
    setDetailOpen(true);
  }

  const tpl: RdCardTemplateWithDetails | undefined = detailQ.data;

  useEffect(() => {
    const t = detailQ.data;
    if (!t) return;
    setGenName(t.name);
    setGenDesc(t.description ?? "");
    setGenReqTypeKlsId(t.request_type_kls_id != null ? t.request_type_kls_id : "");
    setGenOrgKlsId(t.organization_kls_id != null ? t.organization_kls_id : "");
    setGenDefault(t.is_default);
    setGenActive(t.is_active);
  }, [detailQ.data?.id, detailQ.data?.updated_at]);

  const saveGeneral = useCallback(() => {
    const t = detailQ.data;
    if (!t) return;
    if (t.status === "draft") {
      updateTplM.mutate({
        id: t.id,
        body: {
          name: genName.trim(),
          description: genDesc.trim() || null,
          request_type_kls_id: genReqTypeKlsId === "" ? null : genReqTypeKlsId,
          organization_kls_id: genOrgKlsId === "" ? null : genOrgKlsId,
          is_default: genDefault,
          is_active: genActive,
        },
      });
      return;
    }
    if (t.status === "published" || t.status === "archived") {
      updateTplM.mutate({
        id: t.id,
        body: {
          name: genName.trim(),
          description: genDesc.trim() || null,
          is_default: genDefault,
          is_active: genActive,
        },
      });
    }
  }, [
    detailQ.data,
    genActive,
    genDefault,
    genDesc,
    genName,
    genOrgKlsId,
    genReqTypeKlsId,
    updateTplM,
  ]);

  const downloadRdExport = useCallback(
    async (id: number, code: string) => {
      try {
        const data = await rdCardTemplatesApi.exportTemplateJson(id);
        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `rd-card-template-${code}.json`;
        a.click();
        URL.revokeObjectURL(url);
      } catch (e) {
        toast({
          title: "Экспорт",
          description: e instanceof Error ? e.message : "Ошибка",
          variant: "destructive",
        });
      }
    },
    [toast],
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2 justify-between">
        <p className="text-sm text-muted-foreground max-w-2xl">
          Шаблоны заявок РД: таблицы{" "}
          <code className="text-xs bg-muted px-1 rounded">templates.rd_card_*</code>. Конструктор — право{" "}
          <code className="text-xs bg-muted px-1 rounded">rd_card_templates.manage</code>; публикация, архив и
          восстановление — <code className="text-xs bg-muted px-1 rounded">templates.publish</code> (как у ОИ).
          Структура опубликованного шаблона меняется через клонирование; название и флаги default/active можно
          править без клона.
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[160px] h-9">
              <SelectValue placeholder="Статус" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">Все статусы</SelectItem>
              <SelectItem value="draft">Черновик</SelectItem>
              <SelectItem value="published">Опубликован</SelectItem>
              <SelectItem value="archived">Архив</SelectItem>
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={() => listQ.refetch()} disabled={listQ.isFetching}>
            <RefreshCw className={`h-4 w-4 mr-1 ${listQ.isFetching ? "animate-spin" : ""}`} />
            Обновить
          </Button>
          <Button size="sm" onClick={() => setCreateOpen(true)}>
            <Plus className="h-4 w-4 mr-1" />
            Новый черновик
          </Button>
          <input
            ref={importInputRef}
            type="file"
            accept="application/json,.json"
            className="hidden"
            onChange={async (ev) => {
              const f = ev.target.files?.[0];
              ev.target.value = "";
              if (!f) return;
              try {
                const text = await f.text();
                importM.mutate({ text, onConflict: "suffix" });
              } catch (e) {
                toast({
                  title: "Файл",
                  description: e instanceof Error ? e.message : "Не удалось прочитать файл",
                  variant: "destructive",
                });
              }
            }}
          />
          <Button
            variant="outline"
            size="sm"
            type="button"
            disabled={importM.isPending}
            onClick={() => importInputRef.current?.click()}
          >
            <Upload className="h-4 w-4 mr-1" />
            Импорт JSON
          </Button>
        </div>
      </div>

      {listQ.isLoading ? (
        <CardTemplateTableListSkeleton rows={6} />
      ) : rows.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">Шаблоны не найдены</p>
      ) : (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Код</TableHead>
              <TableHead>Название</TableHead>
              <TableHead className="hidden md:table-cell">Тип заявки</TableHead>
              <TableHead>Статус</TableHead>
              <TableHead className="text-center w-[72px]">Версия</TableHead>
              <TableHead className="text-center w-[72px]">Дефолт</TableHead>
              <TableHead className="hidden lg:table-cell">Опубликован</TableHead>
              <TableHead className="text-right">Действия</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id}>
                <TableCell className="font-mono text-xs">{r.code}</TableCell>
                <TableCell>{r.name}</TableCell>
                <TableCell className="hidden md:table-cell text-sm text-muted-foreground">
                  {r.request_type_kls_id == null ? (
                    <span className="text-foreground">Все типы</span>
                  ) : (
                    <>KLS {r.request_type_kls_id}</>
                  )}
                </TableCell>
                <TableCell>
                  <Badge variant={STATUS_BADGE_VARIANT[r.status as CardTemplateStatus]}>
                    {STATUS_LABELS[r.status as CardTemplateStatus]}
                  </Badge>
                </TableCell>
                <TableCell className="text-center text-sm">{r.version}</TableCell>
                <TableCell className="text-center">{r.is_default ? "✓" : ""}</TableCell>
                <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                  {formatPublishedDate(r.published_at)}
                </TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center justify-end gap-1">
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Открыть"
                      type="button"
                      onClick={() => openDetail(r)}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-7 w-7"
                      title="Клонировать"
                      type="button"
                      onClick={() => beginClone(r)}
                    >
                      <Copy className="h-3.5 w-3.5" />
                    </Button>
                    {r.status === "draft" && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-green-600"
                          title="Опубликовать"
                          type="button"
                          onClick={() => publishM.mutate(r.id)}
                          disabled={publishM.isPending}
                        >
                          <CheckCircle2 className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          title="Удалить черновик"
                          type="button"
                          onClick={() => setDeleteTarget({ id: r.id, fromArchive: false })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                    {r.status === "published" && (
                      <Button
                        size="icon"
                        variant="ghost"
                        className="h-7 w-7 text-amber-600"
                        title="Архивировать"
                        type="button"
                        onClick={() => archiveM.mutate(r.id)}
                        disabled={archiveM.isPending}
                      >
                        <Archive className="h-3.5 w-3.5" />
                      </Button>
                    )}
                    {r.status === "archived" && (
                      <>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-emerald-600"
                          title="Восстановить из архива"
                          type="button"
                          onClick={() => setRestoreTarget(r)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" />
                        </Button>
                        <Button
                          size="icon"
                          variant="ghost"
                          className="h-7 w-7 text-destructive"
                          title="Удалить из базы (необратимо)"
                          type="button"
                          onClick={() => setDeleteTarget({ id: r.id, fromArchive: true })}
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      </>
                    )}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Новый шаблон РД</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3 py-2">
            <div>
              <Label>Код (латиница)</Label>
              <Input value={newCode} onChange={(e) => setNewCode(e.target.value)} placeholder="rd_req_default" />
            </div>
            <div>
              <Label>Название</Label>
              <Input value={newName} onChange={(e) => setNewName(e.target.value)} />
            </div>
            <div className="grid gap-4 md:grid-cols-2">
              <div className="min-w-0 md:col-span-2">
                <Label>Тип заявки (KLS)</Label>
                <div className="mt-1.5">
                  <RdRequestTypeKlsCombobox
                    value={newReqTypeKlsId}
                    onValueChange={setNewReqTypeKlsId}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Справочник квалификатора 320. Пусто — шаблон для всех типов заявок.
                </p>
              </div>
              <div className="min-w-0 md:col-span-2">
                <Label>Организация (KLS)</Label>
                <div className="mt-1.5">
                  <OrganizationKlsCombobox
                    value={newOrgKlsId}
                    onValueChange={setNewOrgKlsId}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  Справочник квалификатора 336. Пусто — для всех организаций.
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              Отмена
            </Button>
            <Button
              disabled={!newCode.trim() || !newName.trim() || createM.isPending}
              onClick={() => createM.mutate()}
            >
              Создать
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => {
          if (!open) setDeleteTarget(null);
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить шаблон РД?</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="space-y-3 text-sm text-muted-foreground">
                {deleteTarget?.fromArchive ? (
                  <>
                    <p>
                      Вы удаляете <strong className="text-foreground">архивную</strong> запись целиком из базы.
                      Это действие необратимо.
                    </p>
                    <p>
                      Вместе с шаблоном будут удалены секции, поля и привязки к колонкам заявок. Иногда разумнее
                      оставить запись в архиве как учётный след.
                    </p>
                  </>
                ) : (
                  <p>
                    Черновик будет удалён безвозвратно вместе со структурой секций и полей. Восстановить его нельзя.
                  </p>
                )}
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => deleteTarget && deleteM.mutate(deleteTarget.id)}
              disabled={deleteM.isPending}
            >
              {deleteTarget?.fromArchive ? "Удалить навсегда" : "Удалить"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={restoreTarget !== null}
        onOpenChange={(open) => {
          if (!open) {
            setRestoreTarget(null);
            setRestoreReason("");
            setRestoreMode("simple");
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Восстановить из архива</DialogTitle>
          </DialogHeader>
          {restoreTarget && (
            <div className="space-y-4 py-2">
              <p className="text-sm text-muted-foreground">
                Шаблон{" "}
                <span className="font-mono text-foreground">{restoreTarget.code}</span>
                {restoreTarget.is_default ? (
                  <>
                    {" "}
                    помечен как «по умолчанию». Если для той же связки типа заявки и организации уже есть
                    опубликованный default (включая универсальный шаблон), выберите стратегию ниже.
                  </>
                ) : (
                  <> будет снова опубликован.</>
                )}
              </p>

              {restoreTarget.is_default && (
                <RadioGroup
                  value={restoreMode}
                  onValueChange={(v) => setRestoreMode(v as RdRestoreMode)}
                  className="gap-3"
                >
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="simple" id="rd-restore-simple" className="mt-0.5" />
                    <Label htmlFor="rd-restore-simple" className="font-normal leading-snug cursor-pointer">
                      Обычное восстановление. При конфликте с действующим default сервер вернёт ошибку.
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="replace" id="rd-restore-replace" className="mt-0.5" />
                    <Label htmlFor="rd-restore-replace" className="font-normal leading-snug cursor-pointer">
                      Заменить default: снять признак с текущего опубликованного шаблона в этой ячейке.
                    </Label>
                  </div>
                  <div className="flex items-start gap-2">
                    <RadioGroupItem value="non_default" id="rd-restore-nod" className="mt-0.5" />
                    <Label htmlFor="rd-restore-nod" className="font-normal leading-snug cursor-pointer">
                      Восстановить без признака «по умолчанию».
                    </Label>
                  </div>
                </RadioGroup>
              )}

              <div>
                <Label htmlFor="rd-restore-reason">Комментарий (необязательно)</Label>
                <Textarea
                  id="rd-restore-reason"
                  value={restoreReason}
                  onChange={(e) => setRestoreReason(e.target.value)}
                  rows={2}
                  className="mt-1.5"
                  placeholder="Причина восстановления"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setRestoreTarget(null);
                setRestoreReason("");
                setRestoreMode("simple");
              }}
            >
              Отмена
            </Button>
            <Button
              onClick={() => {
                if (!restoreTarget) return;
                const payload: CardTemplateRestoreRequest = {
                  reason: restoreReason.trim() || null,
                  replace_default: restoreMode === "replace",
                  restore_as_non_default: restoreMode === "non_default",
                };
                restoreM.mutate({ id: restoreTarget.id, payload });
              }}
              disabled={restoreM.isPending}
            >
              {restoreM.isPending ? "Восстановление..." : "Восстановить"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Sheet
        open={detailOpen}
        onOpenChange={(open) => {
          setDetailOpen(open);
          if (!open) setSelected(null);
        }}
      >
        <SheetContent
          side="right"
          className="flex h-full max-w-none flex-col gap-0 overflow-hidden border-l p-0 sm:max-w-none data-[state=open]:slide-in-from-right"
          style={{ width: `${sheetWidthPx}px` }}
        >
          <SheetHeader className="shrink-0 space-y-3 border-b px-6 pb-4 pr-14 pt-4 text-left">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <SheetTitle>Редактирование шаблона</SheetTitle>
                {selected ? (
                  <p className="text-xs text-muted-foreground font-mono mt-1">{selected.code}</p>
                ) : null}
              </div>
              <div className="flex flex-wrap items-center gap-1">
                <span className="text-muted-foreground mr-1 text-xs">Ширина</span>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.narrow))}
                >
                  Узкая
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.standard))}
                >
                  Стандарт
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs"
                  onClick={() => setSheetWidthPx(clampSheetWidth(SHEET_WIDTH_PRESETS.wide))}
                >
                  Широкая
                </Button>
              </div>
            </div>
          </SheetHeader>

          {detailQ.isLoading ? (
            <RdCardTemplateDetailBodySkeleton />
          ) : tpl ? (
            <>
              <Tabs
                key={selected?.id ?? 0}
                defaultValue="general"
                className="flex min-h-0 flex-1 flex-col overflow-hidden"
              >
                <TabsList className="mx-6 mt-3 h-auto shrink-0 flex-wrap justify-start gap-1 rounded-lg bg-muted p-1">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <TabsTrigger value="general" className="gap-1">
                        Общее
                        <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left">
                      Код, название, тип заявки (KLS), организация, признаки шаблона.
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <TabsTrigger value="sections" className="gap-1">
                        Секции
                        <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left">
                      Блоки карточки заявки РД.
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <TabsTrigger value="fields" className="gap-1">
                        Поля
                        <HelpCircle className="h-3.5 w-3.5 opacity-60" />
                      </TabsTrigger>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="max-w-xs text-left">
                      Поля и привязка к колонкам req.request / req.bow.
                    </TooltipContent>
                  </Tooltip>
                </TabsList>

                <TabsContent
                  value="general"
                  className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 data-[state=inactive]:hidden"
                >
                  <div className="space-y-4 py-2 text-sm">
                    {tpl.status !== "draft" && selected && (
                      <Alert>
                        <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <span>
                            Структура (секции, поля) меняется только через клонирование. Название, описание,
                            признаки «по умолчанию» и «активен» можно обновить здесь (как у опубликованных шаблонов
                            ОИ).
                          </span>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() => beginClone(selected)}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Клонировать в черновик
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    <Alert className="border-muted bg-muted/30">
                      <AlertDescription>
                        Код шаблона задаётся при создании и не меняется. Для черновика сохраните метаданные кнопкой
                        «Обновить» внизу панели.
                      </AlertDescription>
                    </Alert>

                    <div className="grid gap-4 md:grid-cols-2">
                      <div className="space-y-2">
                        <Label>Код</Label>
                        <Input readOnly className="bg-muted/50 font-mono text-sm" value={tpl.code} />
                      </div>
                      <div className="flex flex-wrap items-end gap-2 pb-1 text-xs text-muted-foreground">
                        <span>Версия {tpl.version}</span>
                        <Badge variant={STATUS_BADGE_VARIANT[tpl.status as CardTemplateStatus]}>
                          {STATUS_LABELS[tpl.status as CardTemplateStatus]}
                        </Badge>
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="rd-tpl-name">Название *</Label>
                        <Input
                          id="rd-tpl-name"
                          value={genName}
                          onChange={(e) => setGenName(e.target.value)}
                          disabled={false}
                        />
                      </div>
                      <div className="space-y-2 md:col-span-2">
                        <Label htmlFor="rd-tpl-desc">Описание</Label>
                        <Textarea
                          id="rd-tpl-desc"
                          rows={3}
                          value={genDesc}
                          onChange={(e) => setGenDesc(e.target.value)}
                          disabled={false}
                          className="resize-y min-h-[72px]"
                        />
                      </div>
                      <div className="space-y-2 min-w-0">
                        <Label>Тип заявки (KLS)</Label>
                        <div className="mt-0">
                          <RdRequestTypeKlsCombobox
                            value={genReqTypeKlsId}
                            onValueChange={setGenReqTypeKlsId}
                            disabled={tpl.status !== "draft"}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Квалификатор 320. Пусто — все типы заявок.
                        </p>
                      </div>
                      <div className="space-y-2 min-w-0">
                        <Label>Организация (KLS)</Label>
                        <div className="mt-0">
                          <OrganizationKlsCombobox
                            value={genOrgKlsId}
                            onValueChange={setGenOrgKlsId}
                            disabled={tpl.status !== "draft"}
                          />
                        </div>
                        <p className="text-xs text-muted-foreground">
                          Квалификатор 336. Пусто — все организации.
                        </p>
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2">
                        <Checkbox
                          id="rd-tpl-default"
                          checked={genDefault}
                          onCheckedChange={(v) => setGenDefault(v === true)}
                          disabled={false}
                        />
                        <Label htmlFor="rd-tpl-default" className="text-sm font-normal cursor-pointer">
                          По умолчанию для связки тип заявки / организация
                        </Label>
                      </div>
                      <div className="flex items-center gap-2 md:col-span-2">
                        <Checkbox
                          id="rd-tpl-active"
                          checked={genActive}
                          onCheckedChange={(v) => setGenActive(v === true)}
                          disabled={false}
                        />
                        <Label htmlFor="rd-tpl-active" className="text-sm font-normal cursor-pointer">
                          Активен
                        </Label>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-2 border-t pt-4">
                      {tpl.status === "draft" && (
                        <Button
                          size="sm"
                          variant="secondary"
                          type="button"
                          onClick={() => publishM.mutate(tpl.id)}
                        >
                          Опубликовать
                        </Button>
                      )}
                      {tpl.status === "published" && (
                        <Button
                          size="sm"
                          variant="outline"
                          type="button"
                          onClick={() => archiveM.mutate(tpl.id)}
                          disabled={archiveM.isPending}
                        >
                          <Archive className="h-4 w-4 mr-1" />
                          Архивировать
                        </Button>
                      )}
                      {tpl.status === "archived" && selected && (
                        <Button
                          size="sm"
                          variant="secondary"
                          type="button"
                          onClick={() => setRestoreTarget(selected)}
                        >
                          <RotateCcw className="h-4 w-4 mr-1" />
                          Восстановить из архива
                        </Button>
                      )}
                    </div>
                  </div>
                </TabsContent>

                <TabsContent
                  value="sections"
                  className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 data-[state=inactive]:hidden"
                >
                  <div className="space-y-4 py-2 text-sm">
                    {tpl.status !== "draft" && selected && (
                      <Alert>
                        <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <span>Секции редактируются только в черновике.</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() => beginClone(selected)}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Клонировать
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    {tpl.status === "draft" && (
                      <Button size="sm" variant="outline" type="button" onClick={() => setSectionOpen(true)}>
                        + Секция
                      </Button>
                    )}
                    {tpl.sections.length === 0 ? (
                      <p className="text-sm text-muted-foreground">Нет секций</p>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Код</TableHead>
                            <TableHead>Название</TableHead>
                            <TableHead className="w-[100px]"></TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {tpl.sections.map((s) => (
                            <TableRow key={s.id}>
                              <TableCell className="font-mono text-xs">{s.code}</TableCell>
                              <TableCell>{s.name}</TableCell>
                              <TableCell className="text-right">
                                {tpl.status === "draft" && (
                                  <div className="flex justify-end gap-1">
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8"
                                      aria-label="Редактировать секцию"
                                      type="button"
                                      onClick={() => {
                                        setEditSection(s);
                                        setEditSecCode(s.code);
                                        setEditSecName(s.name);
                                      }}
                                    >
                                      <Pencil className="h-4 w-4" />
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="icon"
                                      className="h-8 w-8 text-destructive"
                                      aria-label="Удалить секцию"
                                      type="button"
                                      onClick={() => setDeleteSection(s)}
                                    >
                                      <Trash2 className="h-4 w-4" />
                                    </Button>
                                  </div>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </TabsContent>

                <TabsContent
                  value="fields"
                  className="min-h-0 flex-1 overflow-y-auto px-6 pb-4 data-[state=inactive]:hidden"
                >
                  <div className="space-y-4 py-2 text-sm">
                    {tpl.status !== "draft" && selected && (
                      <Alert>
                        <AlertDescription className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
                          <span>Поля и привязки редактируются только в черновике.</span>
                          <Button
                            type="button"
                            size="sm"
                            variant="secondary"
                            className="shrink-0"
                            onClick={() => beginClone(selected)}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Клонировать
                          </Button>
                        </AlertDescription>
                      </Alert>
                    )}
                    {tpl.status === "draft" && (
                      <>
                        <Button size="sm" variant="outline" type="button" onClick={() => setFieldOpen(true)}>
                          + Поле
                        </Button>
                        <Alert className="border-muted bg-muted/30">
                          <AlertDescription>
                            По умолчанию значения полей сохраняются в{" "}
                            <strong className="text-foreground">универсальном хранилище</strong> (аналог атрибутов
                            объекта ОИ), без колонки <code className="text-xs bg-muted px-1 rounded">req</code>.
                            Привязка к колонке <span className="font-mono text-xs">request</span> /{" "}
                            <span className="font-mono text-xs">bow</span> — опционально, если нужно дублировать
                            значение в фиксированное поле заявки (whitelist PATCH).
                          </AlertDescription>
                        </Alert>
                      </>
                    )}
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Ключ</TableHead>
                          <TableHead>Колонка req</TableHead>
                          <TableHead className="text-right">Действия</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tpl.fields.map((f) => (
                          <TableRow key={f.id}>
                            <TableCell className="font-mono text-xs">{f.field_key}</TableCell>
                            <TableCell className="text-xs text-muted-foreground">
                              {f.binding ? `${f.binding.target_table}.${f.binding.target_column}` : "Универсально"}
                            </TableCell>
                            <TableCell className="text-right whitespace-nowrap">
                              {tpl.status === "draft" && (
                                <div className="flex justify-end gap-1">
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    type="button"
                                    className="text-foreground"
                                    title="Опционально: колонка req.request или req.bow (иначе — универсальное хранилище)"
                                    onClick={() => {
                                      setBindFieldId(f.id);
                                      setBindTable(f.binding?.target_table ?? "request");
                                      setBindCol(f.binding?.target_column ?? "");
                                      setBindOpen(true);
                                    }}
                                  >
                                    Привязка
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8"
                                    aria-label="Редактировать поле"
                                    type="button"
                                    onClick={() => {
                                      setEditField(f);
                                      setEditFk(f.field_key);
                                      setEditFlabel(f.label);
                                      setEditQualId(String(f.qual_id));
                                      setEditFType(f.field_type);
                                      setEditSectionId(f.section_id != null ? String(f.section_id) : "none");
                                    }}
                                  >
                                    <Pencil className="h-4 w-4" />
                                  </Button>
                                  <Button
                                    variant="ghost"
                                    size="icon"
                                    className="h-8 w-8 text-destructive"
                                    aria-label="Удалить поле"
                                    type="button"
                                    onClick={() => setDeleteField(f)}
                                  >
                                    <Trash2 className="h-4 w-4" />
                                  </Button>
                                </div>
                              )}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </TabsContent>
              </Tabs>

              <SheetFooter className="shrink-0 border-t px-6 py-4 flex flex-wrap gap-2 justify-between">
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" type="button" onClick={() => setDetailOpen(false)}>
                    Отмена
                  </Button>
                  <Button
                    variant="outline"
                    type="button"
                    onClick={() => downloadRdExport(tpl.id, tpl.code)}
                  >
                    Экспорт JSON
                  </Button>
                </div>
                {(tpl.status === "draft" ||
                  tpl.status === "published" ||
                  tpl.status === "archived") && (
                  <Button type="button" onClick={saveGeneral} disabled={updateTplM.isPending}>
                    {updateTplM.isPending ? "Сохранение..." : "Обновить метаданные"}
                  </Button>
                )}
              </SheetFooter>
            </>
          ) : null}
        </SheetContent>
      </Sheet>


      <Dialog open={sectionOpen} onOpenChange={setSectionOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Секция</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Код</Label>
              <Input value={secCode} onChange={(e) => setSecCode(e.target.value)} />
            </div>
            <div>
              <Label>Название</Label>
              <Input value={secName} onChange={(e) => setSecName(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!secCode.trim() || !secName.trim() || addSecM.isPending}
              onClick={() => addSecM.mutate()}
            >
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={fieldOpen} onOpenChange={setFieldOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Поле</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>field_key</Label>
              <Input value={fk} onChange={(e) => setFk(e.target.value)} />
            </div>
            <div>
              <Label>Метка</Label>
              <Input value={flabel} onChange={(e) => setFlabel(e.target.value)} />
            </div>
            <div>
              <Label>qual_id</Label>
              <Input value={qualId} onChange={(e) => setQualId(e.target.value)} />
            </div>
            <div>
              <Label>Тип поля</Label>
              <Select value={fType} onValueChange={setFType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {widgetTypeOptions.map((t) => (
                    <SelectItem key={t.kls_code} value={t.kls_code}>
                      {t.kls_namef}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!fk.trim() || !flabel.trim() || addFieldM.isPending}
              onClick={() => addFieldM.mutate()}
            >
              Добавить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={bindOpen} onOpenChange={setBindOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Опциональная колонка req</DialogTitle>
            <DialogDescription>
              Укажите колонку из whitelist PATCH, если значение должно дублироваться в{" "}
              <span className="font-mono text-foreground">req.request</span> /{" "}
              <span className="font-mono text-foreground">req.bow</span>. Поля без привязки сохраняются в универсальном
              хранилище атрибутов заявки (по аналогии с карточкой объекта).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Таблица</Label>
              <Select value={bindTable} onValueChange={setBindTable}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="request">request</SelectItem>
                  <SelectItem value="bow">bow</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Колонка</Label>
              <Input value={bindCol} onChange={(e) => setBindCol(e.target.value)} placeholder="request_num" />
            </div>
          </div>
          <DialogFooter>
            <Button
              disabled={!bindCol.trim() || bindFieldId == null || bindM.isPending}
              onClick={() => bindM.mutate()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editSection != null}
        onOpenChange={(o) => {
          if (!o) setEditSection(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактирование секции</DialogTitle>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>Код</Label>
              <Input value={editSecCode} onChange={(e) => setEditSecCode(e.target.value)} />
            </div>
            <div>
              <Label>Название</Label>
              <Input value={editSecName} onChange={(e) => setEditSecName(e.target.value)} />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditSection(null)}>
              Отмена
            </Button>
            <Button
              disabled={!editSecCode.trim() || !editSecName.trim() || updateSecM.isPending}
              onClick={() => updateSecM.mutate()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={editField != null}
        onOpenChange={(o) => {
          if (!o) setEditField(null);
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Редактирование поля</DialogTitle>
            <DialogDescription>
              Колонку req при необходимости задайте кнопкой «Привязка» в строке поля. Без привязки значение сохраняется
              универсально (отдельная таблица атрибутов заявки).
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-3">
            <div>
              <Label>field_key</Label>
              <Input value={editFk} onChange={(e) => setEditFk(e.target.value)} />
            </div>
            <div>
              <Label>Метка</Label>
              <Input value={editFlabel} onChange={(e) => setEditFlabel(e.target.value)} />
            </div>
            <div>
              <Label>qual_id</Label>
              <Input value={editQualId} onChange={(e) => setEditQualId(e.target.value)} />
            </div>
            <div>
              <Label>Тип поля</Label>
              <Select value={editFType} onValueChange={setEditFType}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  {widgetTypeOptions.map((t) => (
                    <SelectItem key={t.kls_code} value={t.kls_code}>
                      {t.kls_namef}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Секция</Label>
              <Select value={editSectionId} onValueChange={setEditSectionId}>
                <SelectTrigger>
                  <SelectValue placeholder="Секция" />
                </SelectTrigger>
                <SelectContent className="z-[250]">
                  <SelectItem value="none">Без секции</SelectItem>
                  {(tpl?.sections ?? []).map((s) => (
                    <SelectItem key={s.id} value={String(s.id)}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditField(null)}>
              Отмена
            </Button>
            <Button
              disabled={!editFk.trim() || !editFlabel.trim() || updateFieldM.isPending}
              onClick={() => updateFieldM.mutate()}
            >
              Сохранить
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteSection != null} onOpenChange={(o) => !o && setDeleteSection(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить секцию?</AlertDialogTitle>
            <AlertDialogDescription>
              Секция «{deleteSection?.name}». Поля с этой секцией могут потребовать переназначения.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteSection && deleteSecM.mutate(deleteSection.id)}
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={cloneOpen}
        onOpenChange={(o) => {
          setCloneOpen(o);
          if (!o) {
            setCloneSource(null);
            setCloneCode("");
          }
        }}
      >
        <DialogContent className="z-[230]">
          <DialogHeader>
            <DialogTitle>Клонировать шаблон</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Источник:{" "}
            <span className="font-mono text-foreground">{cloneSource?.code ?? "—"}</span>. Будет создан
            черновик с копией секций, полей и привязок.
          </p>
          <div className="grid gap-2 py-2">
            <Label htmlFor="rd-clone-code">Новый код (латиница, уникальный)</Label>
            <Input
              id="rd-clone-code"
              value={cloneCode}
              onChange={(e) => setCloneCode(e.target.value)}
              placeholder="rd_req_copy"
              autoComplete="off"
            />
          </div>
          <DialogFooter>
            <Button variant="outline" type="button" onClick={() => setCloneOpen(false)}>
              Отмена
            </Button>
            <Button
              type="button"
              disabled={!cloneCode.trim() || cloneSource == null || cloneM.isPending}
              onClick={() =>
                cloneSource && cloneM.mutate({ id: cloneSource.id, code: cloneCode.trim() })
              }
            >
              Создать черновик
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog open={deleteField != null} onOpenChange={(o) => !o && setDeleteField(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Удалить поле?</AlertDialogTitle>
            <AlertDialogDescription>
              Поле <span className="font-mono">{deleteField?.field_key}</span> будет удалено из черновика.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Отмена</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={() => deleteField && deleteFieldM.mutate(deleteField.id)}
            >
              Удалить
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
